import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Header from './components/Layout/Header';
import Login from './components/Auth/Login';
import Accounts from './pages/Accounts';
import Accountant from './pages/Accountant';
import Admin from './pages/Admin';
import Logs from './components/Admin/Logs';
import Details from './pages/Details';
import Correction from './pages/Correction';
import Changelog from './pages/Changelog';
import api from './services/api';
import Orders from './pages/Orders';
import './App.css';

function App() {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    // Выход из системы — вызывается и вручную, и при истечении сессии
    const handleLogout = useCallback(async () => {
        await api.logout();
        setUser(null);
    }, []);

    // При старте приложения пробуем восстановить сессию через refresh-токен
    useEffect(() => {
        const restoreSession = async () => {
            api.loadRefreshToken();

            if (!api.refreshToken) {
                setLoading(false);
                return;
            }

            try {
                // Пробуем обновить access-токен
                await api.refreshAccessToken();

                // Декодируем данные пользователя из нового access-токена
                if (api.accessToken) {
                    const payload = JSON.parse(atob(api.accessToken.split('.')[1]));
                    const userData = {
                        username: payload.username,
                        role: payload.role,
                        user_id: payload.user_id,
                    };
                    localStorage.setItem('user', JSON.stringify(userData));
                    setUser(userData);
                } else {
                    api.clearTokens();
                }
            } catch {
                // Refresh-токен недействителен — сбрасываем сессию
                api.clearTokens();
            } finally {
                setLoading(false);
            }
        };

        restoreSession();
    }, []);

    // Глобальный обработчик истечения сессии (SESSION_EXPIRED из api._fetch)
    useEffect(() => {
        const onSessionExpired = () => {
            handleLogout();
        };

        window.addEventListener('session:expired', onSessionExpired);
        return () => window.removeEventListener('session:expired', onSessionExpired);
    }, [handleLogout]);

    const handleLogin = async (credentials) => {
        const response = await api.login(credentials);

        if (response.message === 'Успешный вход') {
            // Декодируем payload из JWT (без верификации — только для UI)
            const payload = JSON.parse(atob(response.access_token.split('.')[1]));
            const userData = {
                username: payload.username,
                role: payload.role,
                user_id: payload.user_id,
            };

            // Сохраняем токены в api-сервисе и refreshToken в localStorage
            api.setTokens(response.access_token, response.refresh_token);

            // Сохраняем данные пользователя БЕЗ пароля
            localStorage.setItem('user', JSON.stringify(userData));

            setUser(userData);
        } else {
            throw new Error(response.message || 'Ошибка входа');
        }
    };

    if (loading) {
        return <div>Загрузка...</div>;
    }

    // Вспомогательная функция проверки доступа к маршруту
    const hasAccess = (allowedRoles) => allowedRoles.includes(user?.role);

    return (
        <Router>
            <div className="App">
                {user ? (
                    <>
                        <Header user={user} onLogout={handleLogout} />
                        <main className="main-content">
                            <Routes>
                                {/* Лицевые счета — доступны всем */}
                                <Route path="/" element={<Accounts user={user} />} />

                                {/* Приказы — admin, lawyer, accountant, user (гость) */}
                                <Route path="/lawyer" element={
                                    hasAccess(['admin', 'lawyer', 'accountant', 'user']) ?
                                        <Orders user={user} /> : <Navigate to="/" />
                                } />

                                {/* Выписки — admin, accountant */}
                                <Route path="/accountant" element={
                                    hasAccess(['admin', 'accountant']) ?
                                        <Accountant user={user} /> : <Navigate to="/" />
                                } />

                                {/* Детализация — admin, lawyer, accountant, user (гость) */}
                                <Route path="/details" element={
                                    hasAccess(['admin', 'lawyer', 'accountant', 'user']) ?
                                        <Details user={user} /> : <Navigate to="/" />
                                } />

                                {/* Корректировка — admin, lawyer, accountant */}
                                <Route path="/correction" element={
                                    hasAccess(['admin', 'lawyer', 'accountant']) ?
                                        <Correction user={user} /> : <Navigate to="/" />
                                } />

                                {/* Админка — только admin */}
                                <Route path="/admin" element={
                                    user.role === 'admin' ? <Admin /> : <Navigate to="/" />
                                } />

                                {/* Лог — только admin */}
                                <Route path="/logs" element={
                                    user.role === 'admin' ? <Logs /> : <Navigate to="/" />
                                } />

                                {/* Журнал изменений — доступен всем */}
                                <Route path="/changelog" element={<Changelog />} />

                                <Route path="*" element={<Navigate to="/" />} />
                            </Routes>
                        </main>
                    </>
                ) : (
                    <Login onLogin={handleLogin} />
                )}
            </div>
        </Router>
    );
}

export default App;
